import { Component, Inject, OnInit } from '@angular/core';
import { EasementCreateStepComponent } from '@app/ps/construction-objects/components/easement-create-step/easement-create-step.component';
import { UploadFileExtended } from '@app/common/components/fileupload/fileupload.component';
import { AuthService } from '@app/common/services/auth.service';
import { Restangular } from 'ngx-restangular';
import { UploadService } from '@app/common/services/upload.service';
import { AttachmentsComponent } from '@app/common/components/attachments/attachments.component';
import { AttachmentModel } from '@app/common/models/attachmentModel';
import { ListModel } from '@app/common/models/list.model';
import { APP_BRAND } from '@app/common/services/config.service';

@Component({
  selector: 'easement-create-add-files',
  templateUrl: './easement-create-add-files.component.html',
})
export class EasementCreateAddFilesComponent extends EasementCreateStepComponent implements OnInit {

  static readonly MSG_FILES_QUANTITY  = 'Je možné vložit pouze jeden soubor.';
  static readonly MSG_FILES_VALIDATION  = 'Vyčkejte, probíhá validace.';
  static readonly MSG_FILES_VALID  = 'Soubor je validní.';
  static readonly MSG_FILES_NOT_VALID  = 'Soubor není validní.';

  static readonly MSG_GP_VFK_FILES_DEFAULT = 'Vložte soubor přetažením nebo výběrem. Je možné vložit jeden soubor VFK.';
  static readonly MSG_GP_VFK_FILES_EXTENSION = 'Soubor musí být ve formátu VFK.';

  static readonly MSG_GP_CSV_FILES_DEFAULT = 'Vložte soubor přetažením nebo výběrem. Je možné vložit jeden soubor CSV.';
  static readonly MSG_GP_CSV_FILES_EXTENSION = 'Soubor musí být ve formátu CSV.';

  // model
  vfkFiles: UploadFileExtended[] = [];
  csvFiles: UploadFileExtended[] = [];
  vfkMessage = EasementCreateAddFilesComponent.MSG_GP_VFK_FILES_DEFAULT;
  csvMessage = EasementCreateAddFilesComponent.MSG_GP_CSV_FILES_DEFAULT;
  csvErrors: string[] = [];
  vfkErrors: string[] = [];
  vfkValid = false;
  csvValid = false;
  gpFiles: ListModel<AttachmentModel>;
  gpVfkFile: AttachmentModel;

  // aux
  restBaseUrl = this.authService.getActiveApplicationRestUrl();
  authToken = this.authService.getToken();

  constructor(
    private restangular: Restangular,
    private uploadService: UploadService,
    private authService: AuthService,
    @Inject(APP_BRAND) private APP_BRAND: any,
  ) {
    super();

    this.onSubmit = this.onSubmit.bind(this);
    this.onChangeFile = this.onChangeFile.bind(this);
    this.uploadVfk = this.uploadVfk.bind(this);
    this.uploadCsv = this.uploadCsv.bind(this);
  }

  ngOnInit() {
    if (this.data.geometricPlanRequest) {
      this.loadGPFiles();
    }
  }

  async loadGPFiles() {
    if (this.APP_BRAND.NAME === 'RSD') {
      this.gpFiles = await this.restangular
          .all('components')
          .customPOST({ filter: { filters: { geometricPlanRequestId: this.data.geometricPlanRequest.id, cancelled: false, attachmentType: 'GEOMETRIC_PLAN_VFK' }}})
          .toPromise();
    } else {
      this.gpFiles = await this.restangular
        .all('attachments')
        .customPOST({ filter: { filters: { geometricPlanRequestId: this.data.geometricPlanRequest.id, cancelled: false, attachmentType: 'GEOMETRIC_PLAN_VFK' }}})
        .toPromise();
    }
    if (this.gpFiles.itemCount == 1) {
      this.gpVfkFile == this.gpFiles[0];
    }
  }

  onChangeFile(files: UploadFileExtended[], type: 'csv' | 'vfk') {
    const extensionValid = files.every(f => f.extension && f.extension.toLowerCase() === type);

    this[type + 'Message'] = EasementCreateAddFilesComponent.MSG_FILES_VALIDATION;
    this[type + 'Files'] = files;
    this[type + 'Valid'] = false;
    this[type + 'Errors'] = [];


    if (files.length === 0) {
      this[type + 'Message'] = type === 'vfk'
        ? EasementCreateAddFilesComponent.MSG_GP_VFK_FILES_DEFAULT
        : EasementCreateAddFilesComponent.MSG_GP_CSV_FILES_DEFAULT;
      return;
    }

    if (!extensionValid) {
      this[type + 'Message'] = type === 'vfk'
        ? EasementCreateAddFilesComponent.MSG_GP_VFK_FILES_EXTENSION
        : EasementCreateAddFilesComponent.MSG_GP_CSV_FILES_EXTENSION;
      return;
    }

    if (files.length > 1) {
      this[type + 'Message'] = EasementCreateAddFilesComponent.MSG_FILES_QUANTITY;
      return;
    }

    this.validate(files[0], type)
      .then(data => {
        this[type + 'Message'] = EasementCreateAddFilesComponent.MSG_FILES_VALID;
        this[type + 'Valid'] = true;
      })
      .catch(data => {
        this[type + 'Message'] = EasementCreateAddFilesComponent.MSG_FILES_NOT_VALID;
        this[type + 'Errors'] = data.data.detailMsgs ? data.data.detailMsgs : [];
      });
  }

  isFormValid() {
    return !!(
      ((this.vfkFiles.length === 1 && this.vfkValid) || this.gpVfkFile)
      && (this.csvFiles.length === 0 || (this.csvFiles.length === 1 && this.csvValid))
    );
  }

  onSubmit(): Promise<any> {
    return this.uploadVfk()
      .then(data => {
        this.data.vfkId = data.vfkId;

        return (this.csvFiles.length !== 0
          ? this.uploadCsv(data.vfkId)
          : true
        );
      })
      .then(() => {
        this.submitCallback.emit();
      });
  }

  private validate(file: UploadFileExtended, type: string): Promise<any> {
    const typeUrl = type === 'csv' ? '/csv/' : '/';

    return this.uploadService.upload({
      url: this.restBaseUrl + '/vfks' + typeUrl + 'validate',
      data: {
        filename: file.fileEntry.name,
        file: file.fileEntry
      },
      headers: { Authorization: this.authService.getToken() }
    }).progress(event => {
      file.progress = (100.0 * event.loaded / event.total);
    });
  }

  /**
  * Import vfk je proveden buď uploadem souboru nebo volbou přílohy žádosti GP
  */
  private uploadVfk(): Promise<any> {
    if (this.gpVfkFile) {
      return this.attachmentToVfk();
    }
    const file = this.vfkFiles[0].fileEntry;

    return this.uploadService.upload({
      url: this.restBaseUrl + '/vfks/create',
      data: {
        filename: file.name,
        file: file
      },
      headers: { Authorization: this.authService.getToken() }
    });
  }

  private attachmentToVfk(): Promise<any> {
    if (this.APP_BRAND.NAME === 'RSD') {
      return this.restangular
        .one('components', this.gpVfkFile.id).customGET('to-vfk').toPromise();
    } else {
      return this.restangular
        .one('attachments', this.gpVfkFile.id).customGET('to-vfk').toPromise();
    }
  }

  private uploadCsv(vfkId: number): Promise<any> {
    const file = this.csvFiles[0].fileEntry;

    return this.uploadService.upload({
      url: this.restBaseUrl + '/vfks/csv/upload?vfkId=' + vfkId,
      data: {
        filename: file.name,
        file: file,
        vfkId: vfkId
      },
      headers: { Authorization: this.authService.getToken() }
    });
  }

  getAttachmentUrl(attachment: AttachmentModel) {
      return `${AttachmentsComponent.VIEWER_URL}${this.restBaseUrl}/attachments/${attachment.id}/data-pdf%3Ft%3D${this.authToken}`;
    }
}
